import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NoWebshopState } from './_webshopNotice';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import LinkIcon from '@mui/icons-material/Link';
import LanguageIcon from '@mui/icons-material/Language';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import SendIcon from '@mui/icons-material/Send';
import Colors from '../../library/Colors';
import SftGlobalData from '../../library/SftGlobalData';
import { webshopTokens, iconBoxSx, WebshopPageHeader } from './_webshopUi';

const WebshopActionsScreen = (props) => {
    const navigate = useNavigate();
    useEffect(() => {
        // Distinct from the other webshop screens ('webshop') so the left
        // dev/admin SpeedDial (App.js) stays hidden on config/data/connections
        // but shows here. Header + simulator keep working via startsWith('webshop').
        if (props.changeScreen) props.changeScreen('webshop-actions');
    }, []);

    const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);

    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [resendId, setResendId] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendWithBcc, setResendWithBcc] = useState(false);
    const [updateUrlModalOpen, setUpdateUrlModalOpen] = useState(false);
    const [newUrl, setNewUrl] = useState('');
    const [urlBusy, setUrlBusy] = useState(false);
    const [syncBusy, setSyncBusy] = useState(false);

    const { isDark, cardBg, borderColor, textColor, mutedText } = webshopTokens(props.theme);

    const notify = (msg, severity = 1) => {
        if (props.notify) props.notify(msg, 4000, 'top-right', props.theme, severity);
    };

    // The webshop's URL lives on `display_name` in `lic_clients_webshop`. The
    // auth backend stores it as CONCAT(cron_url, '/server/v1/mini-api'); the
    // legacy scripts live at `${cron_url}${path}`, so strip the mini-api
    // suffix to get a clean base.
    const baseUrl = useMemo(() => {
        const url = props.selectedWebshop?.display_name || '';
        return url.replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/$/, '');
    }, [props.selectedWebshop?.display_name]);

    // A row that still says http:// works (the proxy upgrades the scheme
    // itself) but is untidy and used to break every mini-api call: the shop's
    // http->https redirect drops the Authorization header. Flag it and offer
    // the one-click fix via the existing setUrl action.
    const insecureUrl = useMemo(() => {
        const fields = [props.selectedWebshop?.display_name, props.selectedWebshop?.url];
        return fields.some(v => typeof v === 'string' && /^http:\/\//i.test(v.trim()));
    }, [props.selectedWebshop?.display_name, props.selectedWebshop?.url]);
    const [fixingScheme, setFixingScheme] = useState(false);
    const fixScheme = async () => {
        if (!props.selectedWebshop?.id) return;
        setFixingScheme(true);
        try {
            const httpsUrl = 'https://' + baseUrl.replace(/^https?:\/\//i, '');
            const r = await SftGlobalData.post('webshop&action=setUrl', {
                key: props.api,
                webshop_id: props.selectedWebshop.id,
                url: httpsUrl,
            });
            if (r?.success) {
                notify(tr('cfg_act_https_fixed', 'Webshop URL now uses https.'), 1);
                if (props.onProvisioned) props.onProvisioned();
            } else {
                notify(r?.message || tr('cfg_act_https_fix_failed', 'Could not update the URL.'), 3);
            }
        } catch (e) {
            notify(e?.message || tr('cfg_act_https_fix_failed', 'Could not update the URL.'), 3);
        } finally {
            setFixingScheme(false);
        }
    };

    // Build a legacy URL with the standard cache-buster the legacy admin uses
    // (some endpoints, e.g. /admin/schedule-slug-reset, won't fire a fresh
    // job without it because the browser serves a cached response).
    const buildLegacyUrl = (path) => {
        const sep = path.includes('?') ? '&' : '?';
        return `${baseUrl}${path}${sep}__cb_=${Date.now()}`;
    };

    // Open a legacy storefront URL in a new tab. The user is normally already
    // logged into the legacy admin in another tab, so the script picks up
    // their session there. The script's progress output stays visible for
    // diagnostics, the MySft tab keeps its state.
    const openLegacyAction = (path) => {
        if (!baseUrl) { notify(tr('cfg_act_no_webshop_selected', 'No webshop selected'), 1); return; }
        if (!path)    { notify(tr('cfg_act_not_wired', 'Action not wired yet'), 1); return; }
        window.open(buildLegacyUrl(path), '_blank', 'noopener,noreferrer');
    };

    // Fire a legacy URL via a hidden iframe — same effect as opening a tab
    // but the user doesn't see a new browser window. Used for actions where
    // the script's response is just a status flag (e.g. {sent: true}) and
    // the user only needs a "completed" notification. Iframe.onload fires
    // when the script returns, regardless of HTTP status, so we treat that
    // as success — for finer-grained reporting we'd need CORS or a backend
    // proxy.
    // F31: one action at a time — the run buttons stayed clickable while a
    // long-running script was in flight, and each extra click spawned another
    // hidden iframe against the same endpoint.
    const [runningPath, setRunningPath] = useState(null);
    const fireLegacyAction = (path, label) => {
        if (!baseUrl) { notify(tr('cfg_act_no_webshop_selected', 'No webshop selected'), 1); return; }
        if (!path)    { notify(tr('cfg_act_not_wired', 'Action not wired yet'), 1); return; }
        const url = buildLegacyUrl(path);

        notify(`${label}: ${tr('cfg_act_running', 'running…')}`, 0);

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        let done = false;
        setRunningPath(path);
        const finish = (ok, msg) => {
            if (done) return;
            done = true;
            setRunningPath(null);
            notify(msg, ok ? 2 : 1);
            setTimeout(() => iframe.remove(), 100);
        };
        iframe.onload  = () => finish(true,  `${label}: ${tr('cfg_act_completed', 'completed')}`);
        iframe.onerror = () => finish(false, `${label}: ${tr('cfg_act_failed', 'failed')}`);
        // Safety net — if onload never fires (network hang), give up after 30s.
        setTimeout(() => finish(false, `${label}: ${tr('cfg_act_timed_out', 'timed out (no response)')}`), 30000);
        iframe.src = url;
        document.body.appendChild(iframe);
    };

    // Same hidden-iframe trick as fireLegacyAction, but promise-based so the
    // URL change can run its three steps in order. Two flavours: a plain GET
    // (load) and a form POST -- replace_urls.php only acts on POST.
    const runInIframe = (build, timeoutMs = 120000) => new Promise((resolve, reject) => {
        const name = `sft-act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.name = name;
        document.body.appendChild(iframe);
        let done = false;
        const finish = (ok, err) => {
            if (done) return;
            done = true;
            setTimeout(() => iframe.remove(), 100);
            ok ? resolve() : reject(err);
        };
        iframe.onload  = () => finish(true);
        iframe.onerror = () => finish(false, new Error('request failed'));
        setTimeout(() => finish(false, new Error('timed out')), timeoutMs);
        try {
            build(iframe, name);
        } catch (e) {
            finish(false, e);
        }
    });

    const getInIframe  = (url, timeoutMs) => runInIframe((iframe) => { iframe.src = url; }, timeoutMs);
    const postInIframe = (url, fields, timeoutMs) => runInIframe((iframe, name) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        form.target = name;
        form.style.display = 'none';
        Object.entries(fields).forEach(([k, v]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = v;
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        setTimeout(() => form.remove(), 100);
    }, timeoutMs);

    const resendTransaction = () => {
        if (!resendId.trim()) {
            notify(tr('cfg_act_reservation_id_required', 'Reservation ID is required'), 1);
            return;
        }
        // Email is optional — when omitted the storefront script falls back
        // to the recipient stored on the reservation.
        const params = new URLSearchParams({
            reservationId: resendId.trim(),
            withBcc:       resendWithBcc ? 'true' : 'false',
        });
        if (resendEmail.trim()) params.set('email', resendEmail.trim());
        // Per the storefront docs, both reservation and receipt resends go
        // through send-reservation-email.php. The receipt-only "gift voucher"
        // script is documented as "Do not use atm" in the storefront docs.
        // Fire via hidden iframe so the user gets a notify in MySft instead
        // of a tab full of "Get shipment type / Send before / Send done /
        // {sent: true}" debug output.
        fireLegacyAction(
            `/server/v1/scripts/send-reservation-email.php?${params.toString()}`,
            tr('cfg_act_resend_label', 'Resend transaction')
        );
        setResendModalOpen(false);
        setResendId('');
        setResendEmail('');
        setResendWithBcc(false); // don't leak the BCC choice into the next resend
    };

    /**
     * Copy what the shop reported (lic_clients_webshop_info, filled by the
     * webshop_info cron) into lic_clients_webshop -- the table this admin and
     * the mini-api proxy actually read. Scoped to the key's own client, and
     * idempotent on (client_id, db_id, slug), so re-running is harmless.
     *
     * Used on its own ("sync webshop record") and as the last step of the URL
     * change.
     *
     * @returns {Promise<boolean>} whether a webshop came back
     */
    const syncWebshopRecord = async ({ okMessage, emptyMessage } = {}) => {
        const res = await SftGlobalData.post('webshop&action=provision', { key: props.api });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (res?.success && list.length > 0) {
            notify(okMessage || tr('cfg_act_sync_record_done', 'Webshop details synced. The list has been refreshed.'), 2);
            if (props.onProvisioned) props.onProvisioned();
            return list;
        }
        if (res?.success) {
            notify(emptyMessage || tr('cfg_act_sync_record_empty', 'Nothing in the sync data yet — the shop has not reported itself. Check that its cron runs.'), 1);
            return null;
        }
        notify(res?.message || tr('cfg_act_url_sync_failed', 'Syncing the licence database failed.'), 1);
        return null;
    };

    const runSyncWebshopRecord = async () => {
        setSyncBusy(true);
        notify(`${tr('cfg_act_sync_record_title', 'Sync webshop details')}: ${tr('cfg_act_running', 'running…')}`, 0);
        try {
            await syncWebshopRecord();
        } catch (e) {
            notify(`${tr('cfg_act_sync_record_title', 'Sync webshop details')}: ${e?.message || tr('cfg_act_failed', 'failed')}`, 1);
        } finally {
            setSyncBusy(false);
        }
    };

    /**
     * Move a shop to a new domain, in the three steps this actually takes:
     *
     *  1. `scripts/replace_urls.php` on the shop rewrites the old domain in the
     *     DB rows that store absolute URLs (carousel, external_links,
     *     image_grid, information_banners, settings), in statics/ and in the
     *     cheque layouts, then regenerates the statics.
     *  2. `crons/webshop_info.php` reports the shop upstream, and it builds
     *     `cron_url` from the host it is called on -- so it has to run on the
     *     NEW domain, otherwise the old one keeps being reported.
     *  3. auth `webshop&action=provision` copies that cron_url from
     *     lic_clients_webshop_info into lic_clients_webshop (scoped to the key,
     *     idempotent), which is the table this admin and the mini-api proxy
     *     read.
     *
     * Step 1 is not reversible from here, hence the confirm in the dialog.
     */
    const updateWebshopUrl = async () => {
        const target = newUrl.trim().replace(/\/+$/, '');
        if (!target) {
            notify(tr('cfg_act_enter_new_url', 'Please enter a new URL'), 1);
            return;
        }
        if (!baseUrl) {
            notify(tr('cfg_act_no_webshop_selected', 'No webshop selected'), 1);
            return;
        }
        if (!/^https?:\/\//i.test(target)) {
            notify(tr('cfg_act_url_needs_scheme', 'Enter the full URL, including https://'), 1);
            return;
        }
        if (target.toLowerCase() === baseUrl.toLowerCase()) {
            notify(tr('cfg_act_url_unchanged', 'That is already the current URL'), 1);
            return;
        }

        setUrlBusy(true);
        try {
            // Step 0 — the FTP-side half: ENV_PRODUCTION in config.php, the
            // Sitemap line in robots.txt and the old-host lines in .crontab.
            // Runs first: everything after this assumes the new domain is the
            // one the shop identifies with.
            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${tr('cfg_act_url_step_golive', 'updating config, robots.txt and crontab…')}`, 0);
            const golive = await SftGlobalData.post('webshop&action=golive', {
                key: props.api,
                webshop_id: props.selectedWebshop?.id,
                url: target,
            });
            if (!golive?.success) {
                // Report which step failed, then stop: rewriting content URLs
                // while the shop still identifies as the old domain would only
                // make the mess bigger.
                const steps = golive?.data?.steps || [];
                const failed = steps.filter(st => st && st.ok === false).map(st => `${st.step}: ${st.msg}`);
                notify(failed.length
                    ? `${tr('cfg_act_url_golive_failed', 'Server-side go-live failed')} — ${failed.join(' | ')}`
                    : (golive?.message || tr('cfg_act_url_golive_failed', 'Server-side go-live failed')), 1);
                return;
            }

            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${tr('cfg_act_url_step_replace', 'rewriting URLs on the shop…')}`, 0);
            await postInIframe(`${baseUrl}/server/v1/scripts/replace_urls.php`, {
                old_url: baseUrl,
                new_url: target,
                regenerate_statics: '1',
            }, 300000);

            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${tr('cfg_act_url_step_report', 'reporting the new domain…')}`, 0);
            // Best-effort: the report itself can fail (DNS not switched yet)
            // without the rewrite in step 1 being any less done.
            try {
                await getInIframe(`${target}/server/v1/crons/webshop_info.php?__cb_=${Date.now()}`, 120000);
            } catch {
                notify(tr('cfg_act_url_report_failed', 'The new domain did not answer yet — run the sync again once it does.'), 1);
            }

            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${tr('cfg_act_url_step_sync', 'syncing the licence database…')}`, 0);
            const synced = await syncWebshopRecord({
                okMessage: tr('cfg_act_update_url_done', 'URL updated. The webshop list has been refreshed.'),
                emptyMessage: tr('cfg_act_url_sync_empty', 'URLs were rewritten, but the new domain is not in the sync data yet. Re-run this once the shop has reported.'),
            });
            if (synced) {
                // ENV_PRODUCTION doubles as the environment, and that value is
                // the `slug` the licence row is keyed on -- so a go-live yields
                // a NEW row next to the old one. Retire the old one (soft
                // delete, credentials carried over) and point the admin at the
                // new one, otherwise every screen keeps talking to the old
                // domain and the shop shows up twice in the picker.
                let list = synced;
                try {
                    const retired = await SftGlobalData.post('webshop&action=retireOldUrl', {
                        key: props.api,
                        old_url: baseUrl,
                        new_url: target,
                    });
                    if (retired?.success) {
                        if (Array.isArray(retired.data?.webshops)) list = retired.data.webshops;
                        if (retired.data?.retired > 0) {
                            notify(retired.data.credentials_moved
                                ? tr('cfg_act_url_retired_creds', 'The old entry has been retired and its FTP/DB credentials moved to the new one.')
                                : tr('cfg_act_url_retired', 'The old entry has been retired.'), 2);
                        }
                    }
                } catch {
                    // Not fatal: the URL change itself already succeeded.
                    notify(tr('cfg_act_url_retire_failed', 'Could not retire the old entry — remove it manually if the shop shows up twice.'), 1);
                }

                const host = target.replace(/^https?:\/\//i, '').toLowerCase();
                const match = list.find(w => String(w.display_name || w.url || '').toLowerCase().includes(host));
                if (match && props.onSelectWebshop) {
                    props.onSelectWebshop(match);
                } else if (!match) {
                    notify(tr('cfg_act_url_no_new_row', 'The new domain has no entry in the licence database yet — pick it in the webshop selector once it appears.'), 1);
                }
                setUpdateUrlModalOpen(false);
                setNewUrl('');
            }
        } catch (e) {
            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${e?.message || tr('cfg_act_failed', 'failed')}`, 1);
        } finally {
            setUrlBusy(false);
        }
    };

    // ── Action registry ────────────────────────────────────────────────────
    // path = legacy storefront URL relative to the webshop's cron_url.
    // Buttons with `path` open `${baseUrl}${path}` in a new tab; buttons
    // without are intentionally left unwired (no documented endpoint yet)
    // and render a disabled "Run" with a "Not wired yet" badge.
    const ACTIONS = [
        {
            id: 'attr-sync',
            icon: LabelIcon,
            title: tr('cfg_act_attr_sync_title', 'Force attribute sync'),
            description: tr('cfg_act_attr_sync_desc', 'Re-push all product attributes (size, color, material…) from the POS to the webshop.'),
            path: '/server/v1/scripts/attributes/sync',
        },
        {
            id: 'full-product-sync',
            icon: Inventory2Icon,
            title: tr('cfg_act_product_sync_title', 'Force full product sync'),
            description: tr('cfg_act_product_sync_desc', 'Rebuild the entire webshop product catalog — prices, stock, descriptions, everything.'),
            path: '/server/v1/scripts/products/sync',
        },
        {
            id: 'brand-sync',
            icon: BrandingWatermarkIcon,
            title: tr('cfg_act_brand_sync_title', 'Force brand logo sync'),
            description: tr('cfg_act_brand_sync_desc', 'Re-upload every brand logo to the webshop from the POS catalog.'),
            path: '/server/v1/crons/brand_logo_sync.php',
        },
        {
            id: 'image-sync',
            icon: PhotoLibraryIcon,
            title: tr('cfg_act_image_sync_title', 'Force image sync'),
            description: tr('cfg_act_image_sync_desc', 'Re-push all product images to the webshop. Use when product images appear broken or missing.'),
            path: '/server/v1/scripts/images/process',
        },
        {
            id: 'reservation-sync',
            icon: ReceiptLongIcon,
            title: tr('cfg_act_reservation_sync_title', 'Process queued reservations'),
            description: tr('cfg_act_reservation_sync_desc', 'Re-send pending reservations and receipts that failed to reach the webshop.'),
            path: '/server/v1/crons/process_queued_reservations.php',
        },
        {
            id: 'resend-transaction',
            icon: ForwardToInboxIcon,
            title: tr('cfg_act_resend_title', 'Resend reservation / receipt'),
            description: tr('cfg_act_resend_desc', 'Manually resend a specific transaction email. Useful for testing template changes or replacing a wrong email address.'),
            onClick: () => setResendModalOpen(true),
        },
        {
            id: 'reset-images',
            icon: RestorePageIcon,
            title: tr('cfg_act_reset_images_title', 'Reset images'),
            description: tr('cfg_act_reset_images_desc', 'Move product images back to the upload folder. They will be re-processed on the next sync.'),
            destructive: true,
            // No documented endpoint yet.
        },
        {
            id: 'slug-reset',
            icon: LinkIcon,
            title: tr('cfg_act_slug_reset_title', 'Slug reset'),
            description: tr('cfg_act_slug_reset_desc', 'Regenerate the URL slugs for every product. Existing customer bookmarks may break.'),
            destructive: true,
            path: `/server/v1/admin/schedule-slug-reset?lng=${props.lang || 'nl'}`,
        },
        {
            id: 'sync-webshop-record',
            icon: CloudSyncIcon,
            title: tr('cfg_act_sync_record_title', 'Sync webshop details'),
            description: tr('cfg_act_sync_record_desc', 'Pull what the shop reported (URL, environment) into the licence database. Use this after a domain change or when the shop is missing from the list.'),
            onClick: runSyncWebshopRecord,
            busy: () => syncBusy,
        },
        {
            id: 'update-url',
            icon: LanguageIcon,
            title: tr('cfg_act_update_url_title', 'Update webshop URL'),
            description: tr('cfg_act_update_url_desc2', 'Move the shop to a new domain: rewrite the stored URLs, let the shop report itself, and update the licence database.'),
            destructive: true,
            onClick: () => setUpdateUrlModalOpen(true),
        },
        {
            id: 'update-version',
            icon: SystemUpdateAltIcon,
            title: tr('cfg_act_update_version_title', 'Updates uitvoeren'),
            description: tr('cfg_act_update_version_desc', 'Open de setup-wizard om de laatste updates (mini-api + migraties) op de shop uit te voeren. Veilig — raakt geen data.'),
            onClick: () => navigate('/' + (props.lang || 'nl') + '/webshop-setup'),
        },
    ];

    // No webshop linked → the action cards would all be disabled anyway; show
    // an explicit empty-state instead.
    if (!props.selectedWebshop) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <NoWebshopState t={props.t} api={props.api} onProvisioned={props.onProvisioned} notify={props.notify} theme={props.theme} />
                </Box>
            </div>
        );
    }

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <WebshopPageHeader
                t={props.t}
                theme={props.theme}
                title={tr('cfg_act_title', 'Webshop actions')}
                description={tr('cfg_act_desc', 'Admin actions that trigger server-side syncs or maintenance jobs. Destructive actions are marked in red.')}
            />

            {insecureUrl && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={(
                        <Button color="inherit" size="small" onClick={fixScheme} disabled={fixingScheme}>
                            {fixingScheme ? <CircularProgress size={16} color="inherit" /> : tr('cfg_act_https_fix', 'Switch to https')}
                        </Button>
                    )}
                >
                    {tr('cfg_act_https_warning', 'This webshop is registered with an http:// URL. Calls still work, but the URL should be https everywhere.')}
                </Alert>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                gap: 1.5,
            }}>
                {ACTIONS.map((action) => {
                    const Icon = action.icon;
                    // An action is "wired" if it has a path or a custom onClick.
                    // Cards that have neither stay visible (so we don't lose the
                    // intent) but the Run button is disabled with a hint.
                    const wired = !!(action.path || action.onClick);
                    // Actions that run through their own async handler report
                    // progress via `busy()`; iframe actions via runningPath.
                    const actionBusy = action.busy ? action.busy() : runningPath === action.path;
                    const disabled = !props.selectedWebshop || !wired;
                    return (
                        <Paper
                            key={action.id}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${borderColor}`,
                                backgroundColor: cardBg,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                transition: 'box-shadow 150ms, transform 150ms, border-color 150ms',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                    transform: 'translateY(-2px)',
                                    borderColor: action.destructive ? Colors.def_red : Colors.sft_blue,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={iconBoxSx(isDark, action.destructive ? Colors.def_red : Colors.sft_blue)}>
                                    <Icon />
                                </Box>
                                {!wired && (
                                    <Chip label={tr('cfg_act_not_wired_chip', 'Not wired yet')} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                                )}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColor, fontSize: '0.9375rem' }}>
                                {action.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: mutedText, flexGrow: 1, lineHeight: 1.45, fontSize: '0.85rem' }}>
                                {action.description}
                            </Typography>
                            <Tooltip title={!props.selectedWebshop ? tr('cfg_act_tip_select_webshop', 'Select a webshop first') : (!wired ? tr('cfg_act_tip_no_endpoint', 'No backend endpoint yet') : '')}>
                                <span style={{ alignSelf: 'flex-start' }}>
                                    <Button
                                        size="small"
                                        variant={action.destructive ? 'outlined' : 'contained'}
                                        color={action.destructive ? 'error' : 'primary'}
                                        disabled={disabled || runningPath !== null || actionBusy}
                                        startIcon={actionBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
                                        onClick={() => {
                                            if (action.onClick) action.onClick();
                                            else openLegacyAction(action.path);
                                        }}
                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                    >
                                        {tr('cfg_act_run', 'Run')}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Paper>
                    );
                })}
            </Box>

            <Dialog
                open={resendModalOpen}
                onClose={() => setResendModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tr('cfg_act_resend_title', 'Resend reservation / receipt')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                        {tr('cfg_act_resend_dialog_desc', "Opens the storefront's send-reservation-email script in a new tab. Useful for testing template changes or replacing a wrong recipient.")}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            label={tr('cfg_act_resend_id_label', 'Reservation / receipt ID')}
                            size="small"
                            fullWidth
                            value={resendId}
                            onChange={(e) => setResendId(e.target.value)}
                            autoFocus
                        />
                        <TextField
                            label={tr('cfg_act_resend_email_label', 'Recipient email (optional)')}
                            size="small"
                            type="email"
                            fullWidth
                            value={resendEmail}
                            onChange={(e) => setResendEmail(e.target.value)}
                            placeholder="customer@example.com"
                            helperText={tr('cfg_act_resend_email_help', 'Leave empty to use the recipient stored on the reservation.')}
                        />
                        <FormControlLabel
                            control={
                                <Switch color="success"
                                    checked={resendWithBcc}
                                    onChange={(e) => setResendWithBcc(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: textColor }}>
                                    {tr('cfg_act_resend_bcc', 'Include BCC recipients')}
                                </Typography>
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setResendModalOpen(false)} sx={{ textTransform: 'none' }}>{tr('cfg_act_cancel', 'Cancel')}</Button>
                    <Button variant="contained" startIcon={<SendIcon />} onClick={resendTransaction} sx={{ textTransform: 'none' }}>
                        {tr('cfg_act_resend_btn', 'Resend')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={updateUrlModalOpen}
                onClose={() => setUpdateUrlModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tr('cfg_act_update_url_title', 'Update webshop URL')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                        {tr('cfg_act_update_url_dialog_desc2', 'Rewrites the old domain everywhere it is stored on the shop (settings, banners, carousel, image grid, external links, statics and cheque layouts), regenerates the statics, then updates the licence database. Rewriting cannot be undone from here — make sure the new domain already serves the shop.')}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            label={tr('cfg_act_current_url', 'Current URL')}
                            size="small"
                            fullWidth
                            value={baseUrl}
                            disabled
                        />
                        <TextField
                            label={tr('cfg_act_new_url', 'New URL')}
                            size="small"
                            fullWidth
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            placeholder="https://shop.example.com"
                            autoFocus
                        />
                        <Typography variant="caption" sx={{ color: mutedText }}>
                            {tr('cfg_act_url_crontab_note', 'Cron jobs for the new domain are set up at the hoster; the old lines are removed here.')}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setUpdateUrlModalOpen(false)} disabled={urlBusy} sx={{ textTransform: 'none' }}>{tr('cfg_act_cancel', 'Cancel')}</Button>
                    <Button variant="contained" color="error" onClick={updateWebshopUrl} disabled={urlBusy}
                        startIcon={urlBusy ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none' }}>
                        {tr('cfg_act_update_url_btn', 'Update URL')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default WebshopActionsScreen;
